import { getCheckPrice, selectBasketCheckPriceItem, selectBasketIsReservationUnlocked, selectBasketItem, selectBasketRedirect, selectBasketSelectedWarehouses, selectBasketWindowError } from 'containers/OrderDetails/basketSlice'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import useVanUpsell, { markVanUpsellShown } from './VanUpsell/useVanUpsell'
import RentalProductPopup from 'containers/Shop/popups/RentalProductPopup'
import { Prompt, useHistory, useParams } from 'react-router-dom'
import { useReduxDispatch, useReduxSelector } from 'store'
import { selectUserId } from 'containers/Login/loginSlice'
import { LOCATION_STATES, STATUS } from 'utils/constants'
import VanUpsellPopup from './VanUpsell/VanUpsellPopup'
import ShortTermCalendar from './Calendar/ShortTerm'
import { isShortTermCapable } from 'utils/shortTerm'
import ArrowBtn from 'components/buttons/ArrowBtn'
import LongTermCalendar from './Calendar/LongTerm'
import Locks from 'containers/OrderDetails/Locks'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import PeriodsToPay from './PeriodsToPay'
import { useEffect, useRef } from 'react'
import Loading from 'components/Loading'
import { selectConfig } from 'appSlice'
import Error from 'components/Error'
import Equipment from './Equipment'
import Discounts from './Discounts'
import Insurance from './Insurance'
import Summary from './Summary'
import Shop from './Shop'
import './style.scss'

const type = process.env.REACT_APP_TYPE

const OrderDetails = ({ isDesktop = false }) => {
  const { id } = useParams<{ id: string }>()
  const config = useReduxSelector(selectConfig)
  const clientId = useReduxSelector(selectUserId)
  const redirect = useReduxSelector(state => selectBasketRedirect(state, id))
  const selectedWarehouses = useReduxSelector(selectBasketSelectedWarehouses)
  const isBasketUnlocked = useReduxSelector(selectBasketIsReservationUnlocked)
  const checkPrice = useReduxSelector(state => selectBasketCheckPriceItem(state, id))
  const basketItem = useReduxSelector(state => selectBasketItem(state, id))
  const windowError = useReduxSelector(state => selectBasketWindowError(state, id))
  const isShortTerm = isShortTermCapable(checkPrice?.shortTerm)

  const { t } = useTranslation()
  const reduxDispatch = useReduxDispatch()
  const { push, goBack, replace } = useHistory()

  const validateLocks = useRef<() => boolean>(null)
  const vanUpsell = useVanUpsell(id)
  const { popup } = usePopupState<{ type?: string, data?: shop.RentalProduct }>()

  const thereAreLocksAvailable = (
    checkPrice?.commonLockInfo ||
    checkPrice?.placeLockInfo ||
    checkPrice?.warehouseLockInfo ||
    (!!checkPrice?.lock?.openingMethods && checkPrice?.lock.openingMethods.length > 0) ||
    (!!checkPrice?.placeLock?.openingMethods && checkPrice?.placeLock.openingMethods.length > 0)
  )

  useEffect(() => {
    if (selectedWarehouses.length > 0) {
      reduxDispatch(getCheckPrice({ id: clientId }))
    }
  }, [clientId, reduxDispatch, selectedWarehouses])

  useEffect(() => {
    const basket = document.querySelector('#basket_container_id')
    basket?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [id])

  useEffect(() => {
    if (redirect === -1) goBack()
    if (redirect && typeof redirect === 'string') replace(redirect)
  }, [goBack, redirect, replace])

  const summary = <>
    <Summary />
    <div className='orderInfo__buttons'>
      <ArrowBtn
        enlarged
        text={t('basic.next')}
        action={() => {
          // najem krótkoterminowy: bez poprawnego okna najmu nie przechodzimy dalej
          if (isShortTerm && (windowError || !basketItem?.startDateTime)) {
            reduxDispatch(addNotification({ type: 'error', message: t('calendar.shortTerm.fixWindow') }))
            return
          }
          if (!thereAreLocksAvailable || validateLocks.current?.()) {
            // ldz01: najpierw „a może bus do tego?”, potem koszyk (raz na pozycję koszyka)
            if (vanUpsell.shouldShow) {
              markVanUpsellShown(id)
              push(window.location.pathname, LOCATION_STATES.popup({ type: 'vanUpsell' }))
              return
            }
            push(window.location.pathname, LOCATION_STATES.popup({ type: 'basket' }))
          }
        }}
      />
    </div>
  </>

  return (
    <>
      <Prompt
        message={({ pathname, state }) => {
          const regex = isBasketUnlocked
            ? /(?=.*(map|order|login|registration|contract|shop))/
            : /^(?!.*(mapdetails|placemap)).*(map|order|login|registration|contract|shop)/
          const isUnlocked = state && typeof state === 'object' && 'unlock' in state ? state.unlock : undefined
          const isBasketEmpty = selectedWarehouses?.length === 0
          return (isBasketEmpty || isUnlocked || pathname.match(regex)) ? true : 'order'
        }}
      />
      <div className={`orderInfo ${isDesktop ? '--desktop' : ''}`}>
        {checkPrice?.status === STATUS.succeed
          ? <>
            {isShortTerm
              ? <ShortTermCalendar />
              : <LongTermCalendar isDesktop={isDesktop} />}
            {(config?.warehouseAccessoriesEnabled && checkPrice?.equipment?.length > 0) && <Equipment />}
            {checkPrice?.insurance && <Insurance />}
            {type !== 'ldz01' && (config?.rentalUpsellEnabled || config?.storeEnabled) && <Shop />}
            {thereAreLocksAvailable && <Locks ref={validateLocks} />}
            <div className='orderInfo__row'>
              <PeriodsToPay />
              {config?.hideDiscountInCart === false && <Discounts />}
            </div>
            {!isDesktop && summary}
          </>
          : checkPrice?.status === STATUS.failed
            ? <Error />
            : <Loading middleOfPage />}
      </div>

      {isDesktop && <div className='orderInfo__aside__wrapper'>
        <div className='orderInfo__aside'>
          {summary}
        </div>
        <OrderDetailsInfo />
      </div>}

      {popup?.type === 'vanUpsell' && vanUpsell.data && <VanUpsellPopup cartId={id} data={vanUpsell.data} />}
      {/* ldz01, jedno auto: wybór terminu bez przechodzenia do sklepiku (z upsellu przed koszykiem albo z koszyka) */}
      {popup?.type === 'rental' && popup.data && <RentalProductPopup
        product={popup.data}
        cartId={id}
        showPhoto
      />}
    </>
  )
}

const OrderDetailsInfo = () => {
  const { id } = useParams<{ id: string }>()
  const data = useReduxSelector(state => selectBasketItem(state, id))

  const { replace } = useHistory()
  const { t } = useTranslation('basket')

  if (data) {
    return (
      <div className='orderInfo__aside__info'>
        <div className='orderInfo__aside__info__left'>
          <i className='icon icon--pin-drop' />
          <b>{data.placeAddress}</b>
        </div>
        <button
          className='button --back --link'
          onClick={() => {
            replace('/home')
          }}>
          <span>{t('chooseAnother')}</span>
        </button>
      </div>
    )
  }

  return null
}

export default OrderDetails